import { Form, FormikProvider, FormikValues, useFormik } from 'formik'
import _ from 'lodash'
import { useConfirm } from 'material-ui-confirm'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '~/hooks/useAuth'
import useRequestWithToast from '~/hooks/useRequestWithToast'
import { useCreateInspection } from '~/modules/inspection/data/useData'
import { inspectionValidationSchema } from '~/modules/inspection/utils/validationSchema'
import { companyExtraFields } from '~/tmp/assetTypes/extra_fields'
import { parseSubObjectIds } from '~/utils/parseSubObjectIds'
import {
  IAircraft,
  ICompany,
  IContactInfo,
  IInspection,
  IInspectionChecklist,
  IUser,
  TInspectionStatus,
} from '~/utils/types'
import { CompanyAssetAuditForm } from '../components/CompanyAssetAuditForm'
import { RealStateAuditForm } from '../components/RealStateAuditForm'
import * as S from './CreateCustomAssetAudit.styles'

export interface ICustomAssetInspectionSubmitValues {
  _id: string | null
  aircraft: IAircraft | null
  general_info: {
    checklist_ref: IInspectionChecklist | null
    operator: ICompany | null
    inspection_company: ICompany | null
    inspector: IUser | null
    country: string | null
    customer?: ICompany | null
    additional_inspectors?: IUser[] | null
    allow_operator_downloads: boolean | null
    checklist?: IInspectionChecklist | null
    contact_info?: IContactInfo | null
    region?: string | null
    due_date?: Date | null
    po_number?: string | null
    po_item?: string | null
    project?: string | null
    inspection_number?: string | null
    aircraft_register?: string | null
    yom?: Date | null
  }
  status: TInspectionStatus
  report_file_path?: string
  extra_fields: {
    [index: string]: any
  }
}

interface ICreateCustomAssetAuditProps {
  assetType: 'Company' | 'Real Estate'
}
export const CreateCustomAssetAudit: FC<ICreateCustomAssetAuditProps> = ({
  assetType = 'Company',
}) => {
  const [willNavigate, setWillNavigate] = useState(false)

  const navigate = useNavigate()
  const confirm = useConfirm()
  const { request } = useRequestWithToast()
  const { t } = useTranslation()

  const { user } = useAuth()
  const userCompany = user?.company as ICompany
  const initialInspectionValues: ICustomAssetInspectionSubmitValues = useMemo(() => {
    const result: ICustomAssetInspectionSubmitValues = {
      _id: null,
      aircraft: null,
      general_info: {
        checklist_ref: null,
        operator: null,
        inspection_company: null,
        country: '',
        inspector: null,
        allow_operator_downloads: null,
        contact_info: null,
        due_date: null,
        inspection_number: null,
        po_item: null,
        po_number: null,
        project: null,
        region: null,
        yom: null,
        additional_inspectors: [],
        customer: userCompany,
      },
      status: 'booked' as TInspectionStatus,
      extra_fields: {},
    }

    for (const extraField of companyExtraFields) {
      result.extra_fields[extraField.field] = extraField.type === 'number' ? 0 : ''
    }

    return result
  }, [userCompany])

  const handleOnSuccess = (newInspection: IInspection) => {
    if (willNavigate) {
      navigate(`/audit/${newInspection._id}`)
    } else {
      formik.resetForm({ values: initialInspectionValues })
      clearForm()
    }
  }

  const { mutateAsync } = useCreateInspection(handleOnSuccess)

  const _handleSubmit = async (values: FormikValues) => {
    const inspection = _.cloneDeep(values)

    if ('_id' in inspection) {
      delete inspection['_id']
    }

    const createInspectionRequest = parseSubObjectIds(inspection, {
      deep: true,
    }) as IInspection

    createInspectionRequest.general_info.country ??= ''

    await mutateAsync(createInspectionRequest)
  }

  const handleSubmitWithToast = async (values: FormikValues) => {
    await request({
      initialText: t('Creating audit...'),
      errorText: t('There was an error creating audit. Please try again!'),
      successText: t('Audit successfully created!'),
      requestFunction: () => _handleSubmit(values),
    })
  }

  const handleSave = () => {
    confirm({
      description: 'Are you sure you want to save this audit?',
    }).then(() => {
      setWillNavigate(false)
      formik.submitForm()
    })
  }

  const handleCreate = () => {
    confirm({
      description: 'Are you sure you want to save this audit?',
    }).then(() => {
      setWillNavigate(true)
      formik.submitForm()
    })
  }

  const formik = useFormik({
    initialValues: initialInspectionValues,
    validationSchema: inspectionValidationSchema,
    onSubmit: handleSubmitWithToast,
  })

  const clearForm = useCallback(() => {
    formik.setFieldValue('aircraft', undefined)
    formik.setFieldValue('general_info.checklist', undefined)
    formik.setFieldValue('general_info.inspection_company', undefined)
    formik.setFieldValue('general_info.operator', undefined)
    formik.setFieldValue('general_info.inspector', undefined)
  }, [formik])

  return (
    <S.Wrapper>
      <S.Subtitle>{t('Creating an audit for')}</S.Subtitle>
      <S.Title>
        {assetType === 'Company' ? t('SMS & Quality Asset') : t('Real Estate Asset')}
      </S.Title>
      <S.FormWrapper>
        <FormikProvider value={formik}>
          <Form>
            {assetType === 'Company' && (
              <CompanyAssetAuditForm onSave={handleSave} onCreate={handleCreate} />
            )}
            {assetType === 'Real Estate' && (
              <RealStateAuditForm onSave={handleSave} onCreate={handleCreate} />
            )}
          </Form>
        </FormikProvider>
      </S.FormWrapper>
    </S.Wrapper>
  )
}
